import React, {useContext, useEffect, useState, useRef} from 'react';
import '../assets/styles/AddChartsDrawer.css';
import Graph from './Graph';
import AuthLogin from '../profile/auth-login';
import { X } from 'lucide-react';

/**
 * AddChartsDrawer Component
 *
 * Slide-in drawer (overlays the Dashboard) that lets users browse and add
 * charts without leaving /my-dashboard. This is the moved-and-wrapped logic
 * from the old standalone AddCharts.js page — the filtering/pagination/caching
 * behavior is unchanged, only the presentation (drawer instead of full page)
 * and the "add" callback (now notifies the Dashboard directly) are new.
 *
 * Features include:
 * - Search and tag-based filtering
 * - Sort modes (all, new, popular)
 * - Pagination with smart page retention on chart removal
 * - Intelligent prefetching with iframe persistence for instant navigation
 * - Cross-mode chart caching (charts stay loaded when switching between All/New/Popular)
 *
 * Performance Architecture:
 * This component uses a THREE-LAYER caching strategy for optimal iframe performance:
 *
 * 1. CHART DATA CACHE (chartDataCache - Map<id, chartData>):
 *    - Stores ALL chart data ever fetched, indexed by chart ID
 *    - NEVER cleared during component lifecycle
 *    - Enables rendering charts from previous modes without re-fetching
 *    - Key to cross-mode persistence: we need the data to render the iframe
 *
 * 2. PAGE-LEVEL TRACKING (loadedPages - Set<number>):
 *    - Tracks which pages should load in the CURRENT view
 *    - Resets when switching sort modes (chart order changes)
 *    - Enables progressive page preloading within a single mode
 *
 * 3. CHART-LEVEL TRACKING (loadedChartIds - Set<number>):
 *    - Tracks which chart IDs have had their iframes loaded
 *    - PERSISTS across sort mode changes
 *    - Combined with chartDataCache, enables instant cross-mode switching
 *
 * CRITICAL: Stable DOM Order
 * To prevent iframe reloads, charts are rendered in a SORTED order by ID.
 * This ensures iframes never move in the DOM, which would cause browser reloads.
 * Visibility is controlled purely via CSS display property.
 *
 * Example: User views "All" charts, then clicks "Popular"
 * - Old behavior: All iframes destroyed and recreated (different DOM order)
 * - New behavior: Iframes stay in same DOM position, only visibility changes
 *
 * Note: because this component only renders (and only fetches) while the
 * drawer is open, the cache above is scoped to a single "drawer open session"
 * rather than persisting across close/reopen — see Phase 1 risk notes in the plan.
 */
const AddChartsDrawer = ({ isOpen, onClose, onChartAdded }) => {
  const ctx = useContext(AuthLogin);

  // Chart data state - current view's chart list
  const [charts, setCharts] = useState([]);
  const [filteredCharts, setFilteredCharts] = useState([]);

  /**
   * Master cache of ALL chart data ever fetched, indexed by chart ID.
   *
   * This is CRITICAL for cross-mode persistence:
   * - When user views "All", all chart data is cached
   * - When user switches to "Popular", charts that were in "All" still have their data
   * - We can render those charts without waiting for API response
   *
   * Structure: Map<chartId, {id, title, url, tags, tooltip, ...}>
   */
  const [chartDataCache, setChartDataCache] = useState(new Map());

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);  // Array of selected tag filters
  const [availableTags, setAvailableTags] = useState([]);  // All unique tags from charts

  // Sort mode state: 'all', 'new', or 'popular'
  const [sortMode, setSortMode] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const chartsPerPage = 6;

  /**
   * Ref to track previous filter values.
   * Used to distinguish between filter changes (should reset page)
   * vs chart removal (should keep current page).
   */
  const prevFiltersRef = useRef({ searchTerm: '', selectedTags: [] });

  /**
   * Set of page numbers whose charts should be rendered in the current sort mode.
   *
   * This tracks page-level loading for the CURRENT view only.
   * When sort mode changes, page positions change (chart at position 1 in "All"
   * might be at position 5 in "Popular"), so we reset this on mode change.
   *
   * Used for: Progressive page preloading within a single sort mode view.
   */
  const [loadedPages, setLoadedPages] = useState(new Set([1]));

  /**
   * Set of chart IDs whose iframes have been loaded at any point.
   *
   * This is the KEY to cross-mode performance optimization:
   * - When user views "All" charts, we track every chart ID that gets loaded
   * - When user switches to "Popular", charts that exist in BOTH lists
   *   are already in loadedChartIds, so their iframes don't reload
   * - This set NEVER resets during the component lifecycle
   *
   * Result: Switching between All/New/Popular becomes nearly instant for overlapping charts.
   */
  const [loadedChartIds, setLoadedChartIds] = useState(new Set());

  // Pagination calculations (declared early for use in effects)
  const totalPages = Math.ceil(filteredCharts.length / chartsPerPage);

  /**
   * Fetch charts from API based on the specified endpoint.
   *
   * IMPORTANT: This function ADDS to chartDataCache rather than replacing it.
   * This preserves chart data from previous modes, enabling cross-mode persistence.
   *
   * @param {string} endpoint - The endpoint name ('all', 'new', or 'popular')
   */
  const fetchCharts = async (endpoint) => {
    try {
      // Map sort mode to appropriate API endpoint
      let url;
      switch(endpoint) {
        case 'new':
          url = `${process.env.REACT_APP_API_URL}/getNewCharts/${ctx.userId}`;
          break;
        case 'popular':
          url = `${process.env.REACT_APP_API_URL}/getPopularCharts/${ctx.userId}`;
          break;
        case 'all':
        default:
          url = `${process.env.REACT_APP_API_URL}/getAllNonUserCharts/${ctx.userId}`;
          break;
      }

      const response = await fetch(url);
      const data = await response.json();

      // ADD to cache (don't replace) - preserves chart data from other modes
      // This is critical for cross-mode iframe persistence
      setChartDataCache(prev => {
        const newCache = new Map(prev);
        data.forEach(chart => newCache.set(chart.id, chart));
        return newCache;
      });

      setCharts(data);
      setFilteredCharts(data);
    } catch (error) {
      console.error('Error fetching charts:', error);
    }
  };

  /**
   * Fetch charts when the drawer opens (or sortMode changes while open).
   * Gated on `isOpen` so we don't hit the catalog endpoints until the user
   * actually opens the drawer.
   */
  useEffect(() => {
    if (ctx.userId && isOpen) {
      fetchCharts(sortMode);
    }
  }, [ctx.userId, sortMode, isOpen]);

  /**
   * Extract unique tags from all charts
   * Updates whenever charts array changes
   */
  useEffect(() => {
    if (charts.length === 0) {
      setAvailableTags([]);
      return;
    }

    // Flatten all tags from all charts and get unique values
    const allTags = charts.flatMap(chart => chart.tags || []);
    const uniqueTags = [...new Set(allTags)];

    // Sort alphabetically for consistent display
    const sortedTags = uniqueTags.sort((a, b) => a.localeCompare(b));

    setAvailableTags(sortedTags);
  }, [charts]);

  /**
   * Filter charts based on search term and selected tags
   * Updates whenever searchTerm, selectedTags, or charts change
   *
   * Filtering logic:
   * - Search term: Must match chart title (case-insensitive)
   * - Tags: OR logic - chart must have ANY of the selected tags
   * - Both filters must pass (AND logic between search and tags)
   *
   * Page Reset Logic:
   * - Only resets to page 1 when filter CRITERIA change (searchTerm, selectedTags)
   * - Does NOT reset when a chart is removed (allows user to stay on current page)
   * - Handles edge case where current page becomes empty after chart removal
   */
  useEffect(() => {
    const filtered = charts.filter(chart => {
      // Filter by search term (must pass if search term exists)
      const matchesSearch = chart.title.toLowerCase().includes(searchTerm.toLowerCase());

      // Filter by tags (OR logic - chart must have ANY selected tag)
      let matchesTags = true;
      if (selectedTags.length > 0) {
        // Check if chart has at least one of the selected tags
        matchesTags = selectedTags.some(selectedTag =>
          chart.tags && chart.tags.includes(selectedTag)
        );
      }

      // Both conditions must pass
      return matchesSearch && matchesTags;
    });

    setFilteredCharts(filtered);

    // Determine if the filter CRITERIA changed (not just chart array)
    // This distinguishes between:
    // 1. User changed search/tags → should reset to page 1
    // 2. User removed a chart → should stay on current page
    const filtersChanged =
      prevFiltersRef.current.searchTerm !== searchTerm ||
      JSON.stringify(prevFiltersRef.current.selectedTags) !== JSON.stringify(selectedTags);

    if (filtersChanged) {
      // Filter criteria changed - reset to page 1 and clear loaded pages cache
      // (chart POSITIONS change after filter, so page numbers are invalid)
      setCurrentPage(1);
      setLoadedPages(new Set([1]));
      // Note: We preserve loadedChartIds because filtering just hides/shows charts,
      // it doesn't change which chart iframes have been loaded
      prevFiltersRef.current = { searchTerm, selectedTags };
    } else {
      // Only charts array changed (chart was removed)
      // Handle edge case: if current page is now empty, go to previous page
      const newTotalPages = Math.ceil(filtered.length / chartsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    }
  }, [searchTerm, selectedTags, charts, currentPage]);

  /**
   * Intelligent page preloading effect
   *
   * This effect manages which pages have their charts mounted in the DOM.
   * Charts are kept mounted (not destroyed) when navigating, providing instant
   * page transitions since iframes don't need to reload.
   *
   * Loading Strategy:
   * 1. Small datasets (≤2 pages): Load ALL pages immediately
   *    - No delay needed since there's minimal resource overhead
   *    - Provides instant navigation in both directions
   *
   * 2. Larger datasets (3+ pages): Progressive loading
   *    - Current page loads immediately
   *    - Next page preloads after 1.5s delay (prioritizes current page resources)
   *    - Previous pages stay loaded once visited (no reload when going back)
   *
   * This approach balances memory usage with user experience.
   */
  useEffect(() => {
    // Early exit if no pages to load
    if (totalPages === 0) return;

    // Strategy 1: Small dataset optimization
    // For 2 pages or fewer, load everything immediately for instant navigation
    if (totalPages <= 2) {
      const allPages = new Set(Array.from({ length: totalPages }, (_, i) => i + 1));
      // Only update if the set has changed to avoid infinite loops
      if (allPages.size !== loadedPages.size ||
          ![...allPages].every(page => loadedPages.has(page))) {
        setLoadedPages(allPages);
      }
      return;
    }

    // Strategy 2: Progressive loading for larger datasets
    // Always ensure current page is loaded
    if (!loadedPages.has(currentPage)) {
      setLoadedPages(prev => new Set([...prev, currentPage]));
    }

    // Preload next page after a delay (if it exists and isn't already loaded)
    const nextPage = currentPage + 1;
    if (nextPage <= totalPages && !loadedPages.has(nextPage)) {
      const prefetchTimer = setTimeout(() => {
        setLoadedPages(prev => new Set([...prev, nextPage]));
      }, 1500); // 1.5s delay prioritizes current page resources

      return () => clearTimeout(prefetchTimer);
    }
  }, [currentPage, totalPages, loadedPages]);

  /**
   * Handle sort mode change (All / New / Popular buttons)
   *
   * Key Performance Optimization:
   * When switching sort modes, we reset loadedPages (because chart ORDER changes),
   * but we DO NOT reset loadedChartIds or chartDataCache (because the same chart IDs
   * may appear in multiple lists, and we want to keep their iframes loaded).
   *
   * Example: Chart #42 is in both "All" and "Popular"
   * - User views "All", Chart #42's iframe loads → added to loadedChartIds + chartDataCache
   * - User clicks "Popular", loadedPages resets, but loadedChartIds keeps #42
   * - Chart #42 in "Popular" view doesn't need to reload - iframe already exists!
   *
   * @param {string} mode - The sort mode ('all', 'new', or 'popular')
   */
  const handleSortModeChange = (mode) => {
    // Validate mode is one of the allowed values
    if (!['all', 'new', 'popular'].includes(mode)) {
      console.error(`Invalid sort mode: ${mode}`);
      return;
    }

    // Update sort mode state (will trigger useEffect to fetch charts)
    setSortMode(mode);

    // Reset to page 1 for the new view
    setCurrentPage(1);

    // Reset PAGE tracking (chart positions change in new sort order)
    // But KEEP loadedChartIds AND chartDataCache - charts that were loaded stay loaded!
    setLoadedPages(new Set([1]));
    // Note: We intentionally do NOT reset loadedChartIds or chartDataCache
  };

  /**
   * Toggle tag selection for filtering
   * @param {string} tag - The tag to toggle
   */
  const handleTagToggle = (tag) => {
    setSelectedTags(prevTags => {
      // If tag is already selected, remove it
      if (prevTags.includes(tag)) {
        return prevTags.filter(t => t !== tag);
      }
      // Otherwise, add it to selected tags
      return [...prevTags, tag];
    });
  };

  /**
   * Reset all filters to default state
   *
   * Clears search term, selected tags, and resets sort mode to 'all'.
   *
   * Performance note:
   * - We reset loadedPages (chart positions will change)
   * - We KEEP loadedChartIds and chartDataCache (charts loaded in other modes may reappear)
   *
   * This means resetting filters is fast for charts the user has already seen.
   */
  const handleResetFilters = () => {
    setSelectedTags([]);
    setSearchTerm('');
    setSortMode('all');

    // Reset page tracking (positions change), but keep chart ID and data caches
    setLoadedPages(new Set([1]));
    // Note: We intentionally preserve loadedChartIds and chartDataCache for cross-mode optimization
  };

  /**
   * Add a chart to the user's dashboard.
   * Also removes the chart from local state and cache, then notifies the
   * Dashboard (via onChartAdded) so it can append the chart to its own grid
   * without a full page reload.
   *
   * @param {number} chartId - The ID of the chart to add
   */
  const handleAddToDashboard = async (chartId) => {
    // Capture the full chart object before it's removed from the cache below.
    const chartToAdd = chartDataCache.get(chartId);

    try {
      // Optimistically remove from local state
      setCharts(prevCharts => prevCharts.filter(chart => chart.id !== chartId));
      setFilteredCharts(prevCharts => prevCharts.filter(chart => chart.id !== chartId));

      // Also remove from caches since it's no longer available to add
      setChartDataCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(chartId);
        return newCache;
      });
      setLoadedChartIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(chartId);
        return newSet;
      });

      const requestBody = {
        chartIdToAdd: chartId
      };

      if (!chartId) {
        throw new Error('chartId is required');
      }

      const url = `${process.env.REACT_APP_API_URL}/addToDashboard/${ctx.userId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const responseText = await response.text();
        throw new Error(`Failed to update dashboard: ${responseText}`);
      }

      if (chartToAdd && onChartAdded) {
        onChartAdded(chartToAdd);
      }
    } catch (error) {
      console.error('Error updating dashboard:', error);
      alert(`Failed to add chart: ${error.message}`);

      // Refresh charts from the current sort mode endpoint
      try {
        await fetchCharts(sortMode);
      } catch (refreshError) {
        console.error('Error refreshing charts:', refreshError);
      }
    }
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || selectedTags.length > 0 || sortMode !== 'all';

  /**
   * Navigate to the next page
   */
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prevPage => prevPage + 1);
    }
  };

  /**
   * Navigate to the previous page
   */
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prevPage => prevPage - 1);
    }
  };

  /**
   * Build the set of chart IDs that should be mounted in the DOM.
   * This combines:
   * 1. Charts from current view on loaded pages (for initial/progressive loading)
   * 2. Previously loaded chart IDs (for cross-mode persistence)
   *
   * @returns {Set<number>} Set of chart IDs to render
   */
  const getChartsToMount = () => {
    const chartIdsToMount = new Set();

    // Add charts from current filtered view that are on loaded pages
    filteredCharts.forEach((chart, index) => {
      const chartPageNumber = Math.floor(index / chartsPerPage) + 1;
      if (loadedPages.has(chartPageNumber)) {
        chartIdsToMount.add(chart.id);
      }
    });

    // Add all previously loaded chart IDs (cross-mode persistence)
    // Only include if we have their data in the cache
    loadedChartIds.forEach(id => {
      if (chartDataCache.has(id)) {
        chartIdsToMount.add(id);
      }
    });

    return chartIdsToMount;
  };

  /**
   * Build a lookup map for chart positions in the current filtered view.
   * Used to determine visibility (only charts on current page are visible).
   *
   * @returns {Map<number, number>} Map of chartId → index in filteredCharts
   */
  const getCurrentViewPositions = () => {
    const positions = new Map();
    filteredCharts.forEach((chart, index) => {
      positions.set(chart.id, index);
    });
    return positions;
  };

  if (!isOpen) {
    return null;
  }

  // Pre-compute which charts to mount and their positions
  const chartsToMount = getChartsToMount();
  const currentViewPositions = getCurrentViewPositions();

  // CRITICAL: Sort chart IDs to maintain STABLE DOM order
  // This prevents iframes from being moved in the DOM (which causes browser reloads)
  const sortedChartIds = [...chartsToMount].sort((a, b) => a - b);

  return (
    <div className="add-charts-drawer">
      <div className="add-charts-drawer-backdrop" onClick={onClose} />
      <div className="add-charts-drawer-panel">
        <div className="add-charts-drawer-header">
          <h2>Add Charts to Dashboard</h2>
          <button className="add-charts-drawer-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="add-charts-drawer-body">
          {/* Search Input and Reset Button */}
          <div className="add-charts-search">
            <input
              type="text"
              placeholder="Search charts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="add-charts-search-input"
            />
            {hasActiveFilters && (
              <button
                className="reset-filter-btn-inline"
                onClick={handleResetFilters}
                title="Reset All Filters"
              >
                Reset Filters
              </button>
            )}
          </div>

          {/* Sort Mode Toggle Buttons */}
          <div className="sort-mode-container">
            <button
              className={`sort-mode-btn ft-pill-btn ${sortMode === 'all' ? 'active' : ''}`}
              onClick={() => handleSortModeChange('all')}
            >
              All
            </button>
            <button
              className={`sort-mode-btn ft-pill-btn ${sortMode === 'new' ? 'active' : ''}`}
              onClick={() => handleSortModeChange('new')}
            >
              New
            </button>
            <button
              className={`sort-mode-btn ft-pill-btn ${sortMode === 'popular' ? 'active' : ''}`}
              onClick={() => handleSortModeChange('popular')}
            >
              Popular
            </button>
          </div>

          {/* Tag Filter Chips */}
          {availableTags.length > 0 && (
            <div className="tag-filter-container">
              <div className="tag-filter-label">Filter by tags:</div>
              <div className="tag-filter-chips">
                {availableTags.map((tag) => (
                  <button
                    key={tag}
                    className={`tag-filter-chip ft-pill-btn ${selectedTags.includes(tag) ? 'active' : ''}`}
                    onClick={() => handleTagToggle(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Filter Summary */}
          <div className="filter-summary">
            {filteredCharts.length === charts.length ? (
              <span>Showing all {charts.length} chart{charts.length !== 1 ? 's' : ''}</span>
            ) : (
              <span>
                Showing {filteredCharts.length} of {charts.length} chart{charts.length !== 1 ? 's' : ''}
                {searchTerm && <span className="filter-detail"> matching "{searchTerm}"</span>}
                {selectedTags.length > 0 && (
                  <span className="filter-detail">
                    {' '}with tag{selectedTags.length !== 1 ? 's' : ''}: {selectedTags.join(', ')}
                  </span>
                )}
              </span>
            )}
          </div>

          {/*
            Charts Grid with Stable DOM Order for Iframe Persistence

            CRITICAL ARCHITECTURE:
            Charts are rendered in a SORTED ORDER by ID to prevent DOM reordering.
            When iframes move in the DOM, browsers reload them - by keeping a stable
            order, we ensure iframes never move once created.

            RENDERING STRATEGY:
            1. Collect all chart IDs that should be mounted (from page loading + previous loads)
            2. Sort these IDs numerically for stable DOM order
            3. Render each chart from the cache, using CSS display to control visibility

            VISIBILITY LOGIC:
            - A chart is VISIBLE only if it's on the current page in the current view
            - Hidden charts keep their iframes loaded (display:none preserves iframe state)
            - This enables instant page navigation and mode switching
          */}
          <div className="add-charts-grid">
            {sortedChartIds.map(chartId => {
              // Get chart data from cache
              const chart = chartDataCache.get(chartId);
              if (!chart) return null;

              // Track this chart as loaded (for cross-mode persistence)
              // Using a ref check to avoid unnecessary state updates during render
              if (!loadedChartIds.has(chartId)) {
                // Use setTimeout to avoid setState during render
                setTimeout(() => {
                  setLoadedChartIds(prev => {
                    if (prev.has(chartId)) return prev; // Avoid unnecessary updates
                    return new Set([...prev, chartId]);
                  });
                }, 0);
              }

              // Determine visibility based on current filtered view
              // Chart is visible only if it's on the current page in the current view
              const indexInCurrentView = currentViewPositions.get(chartId);
              const isInCurrentView = indexInCurrentView !== undefined;
              const currentViewPage = isInCurrentView
                ? Math.floor(indexInCurrentView / chartsPerPage) + 1
                : -1;
              const isVisible = currentViewPage === currentPage;

              return (
                <div
                  key={chartId}
                  className="add-charts-item"
                  style={{
                    // CSS visibility toggle - iframe stays mounted when hidden
                    // display:none preserves iframe state, preventing reloads
                    display: isVisible ? '' : 'none'
                  }}
                >
                  <Graph
                    {...chart}
                    // Only show interactive "+" button on visible charts
                    // Prevents accidental clicks on hidden charts
                    showPlus={isVisible}
                    isPreview
                    // These tiles are consistently small (~240-330px) across every
                    // breakpoint, so a fixed 'xs' watermark tier is accurate without
                    // needing to measure the actual CSS grid cell size in JS.
                    size="xs"
                    onAdd={() => handleAddToDashboard(chartId)}
                  />
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {filteredCharts.length > 0 && totalPages > 1 && (
            <div className="pagination-container">
              <button
                className="pagination-btn ft-pill-btn"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="pagination-btn ft-pill-btn"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}

          {/* Empty State - Updated with filter information */}
          {filteredCharts.length === 0 && (
            <div className="add-charts-empty-state">
              <p>No charts found{hasActiveFilters ? ' matching your filters' : ''}.</p>
              {hasActiveFilters && (
                <>
                  <p className="empty-state-details">
                    {searchTerm && <span>Search: "{searchTerm}"</span>}
                    {searchTerm && selectedTags.length > 0 && <span> • </span>}
                    {selectedTags.length > 0 && (
                      <span>Tags: {selectedTags.join(', ')}</span>
                    )}
                    {(searchTerm || selectedTags.length > 0) && sortMode !== 'all' && <span> • </span>}
                    {sortMode !== 'all' && (
                      <span>Sort: {sortMode.charAt(0).toUpperCase() + sortMode.slice(1)}</span>
                    )}
                  </p>
                  <button
                    className="empty-state-reset-btn"
                    onClick={handleResetFilters}
                  >
                    Reset Filters
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddChartsDrawer;
